import { forwardRef, useEffect, useMemo, useState } from 'react';

import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerProps,
  useDrawerInstance,
  notify,
} from '@cast/design-system';
import {
  CreateSlackConfiguration,
  CreateWebhook,
  DeliveryMethod,
} from '@cast/types';

import { useCurrentOrganization } from '@cast/infrastructure-organization';
import { DestroyDrawerFormGuard } from 'components/drawer/ds';
import {
  useCreateSlackConfiguration,
  useCreateWebhookMutation,
} from 'hooks/mutations/notifications';
import { useGetSlackWorkspaces } from 'hooks/queries/notifications';
import { useChatVisibilityToggle } from 'hooks/useChatVisibilityToggle';

import { DrawerTitle } from './DrawerTitle';
import { AlertForm, makeApiModel, FormModel } from './form';

type Props = DrawerProps & {
  restoredFormState?: FormModel;
};

export const AddAlertDrawer = forwardRef<HTMLDivElement, Props>(
  (drawerProps, ref) => {
    const [formIsDirty, setFormIsDirty] = useState(false);
    const { close, shouldRenderGuard, setOnCloseGuard } = useDrawerInstance();
    const currentOrganization = useCurrentOrganization();
    const defaultAlertName = currentOrganization?.name
      ? `${currentOrganization.name} alerts`
      : undefined;
    const { mutateAsync: createWebhook } = useCreateWebhookMutation(close);
    const { mutateAsync: createSlackConfiguration } =
      useCreateSlackConfiguration(close);
    const { data: slackWorkspacesData } = useGetSlackWorkspaces();
    const restoredFormState = useMemo(() => {
      // Note: URL state param cleanup now handled by Notifications.tsx
      // This component only uses restoredFormState passed via props
      return drawerProps.restoredFormState;
    }, [drawerProps.restoredFormState]);
    useChatVisibilityToggle(!!drawerProps.open);

    useEffect(() => {
      if (formIsDirty) {
        setOnCloseGuard(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formIsDirty]);

    return (
      <Drawer
        ref={ref}
        {...drawerProps}
        size="medium"
        testId="add-alert-drawer"
      >
        <DrawerHeader />
        <DrawerContent flex={1}>
          <DrawerTitle mt={16}>CREATE ALERT</DrawerTitle>
          <AlertForm
            mode="create"
            restoredFormState={restoredFormState}
            defaultName={defaultAlertName}
            onSubmit={async (model) => {
              if (model.deliveryMethod === DeliveryMethod.Slack) {
                // Get the workspace ID from the connected workspace
                const workspaceId = slackWorkspacesData?.items?.[0]?.id;
                if (!workspaceId) {
                  notify.error('Please connect a Slack workspace first');
                  throw new Error('Please connect a Slack workspace first');
                }

                // Add workspaceId to the model
                const modelWithWorkspace = {
                  ...model,
                  slackWorkspaceId: workspaceId,
                };

                return await createSlackConfiguration(
                  makeApiModel(modelWithWorkspace) as CreateSlackConfiguration
                );
              }
              return await createWebhook(makeApiModel(model) as CreateWebhook);
            }}
            onCancel={() => close()}
            onDirty={setFormIsDirty}
          />
        </DrawerContent>

        {shouldRenderGuard && (
          <DestroyDrawerFormGuard
            onContinue={() => {
              setOnCloseGuard(false);
              setTimeout(() => {
                if (formIsDirty) {
                  setOnCloseGuard(true);
                }
              }, 0);
            }}
            onLeave={close}
          />
        )}
      </Drawer>
    );
  }
);

AddAlertDrawer.displayName = 'AddAlertDrawer';
